import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Popover from '@mui/material/Popover';
import classNames from 'classnames';
import ControlledSwitch from '@/components/common/form/ControlledSwitch';
import type ConstituentSearchColumn from '@/constants/ConstituentSearchColumn';
import useCustomFields from '@/lib/hooks/swr/useCustomFields';
import translate from '@/lib/intl/translate';
import type { ConstituentSearchFilterParams } from '@/modules/ConstituentSearch';
import { useQueryProvider } from '@/modules/QueryProvider';
import BaseColumnOptions from '../constants/BaseColumns';
import ColumnsLabelMap from '../constants/ColumnsLabelMap';
import ContributionHistoryColumns from '../constants/ContributionHistoryColumns';
import SocialMediaColumns from '../constants/SocialMediaColumns';
import type ConstituentColumnSelectFormValues from '../types/ConstituentColumnSelectFormValues';
import mapColumnsToDefaultValues from '../utils/mapColumnsToDefaultValues';
import mapFormDataToColumns from '../utils/mapFormDataToColumns';
import ConstituentColumnSelectSubMenu from './ConstituentColumnSelectSubMenu';

function ConstituentColumnSelect() {
  const columnButtonRef = React.useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = React.useState<boolean>(false);

  const { query, updateQuery } =
    useQueryProvider<ConstituentSearchFilterParams>();

  const queryColumns: ConstituentSearchColumn[] = React.useMemo(
    () =>
      'columns' in query && Array.isArray(query.columns) ? query.columns : [],
    [query],
  );

  const queryCustomFieldColumns: string[] = React.useMemo(
    () =>
      'customFieldColumns' in query && Array.isArray(query.customFieldColumns)
        ? query.customFieldColumns
        : [],
    [query],
  );

  const { data: customFieldData } = useCustomFields();

  const defaultValues = React.useMemo(
    () =>
      mapColumnsToDefaultValues(
        queryColumns,
        queryCustomFieldColumns,
        customFieldData || [],
      ),
    [queryColumns, queryCustomFieldColumns, customFieldData],
  );

  const formMethods = useForm<ConstituentColumnSelectFormValues>({
    defaultValues,
  });

  const { reset, handleSubmit, control } = formMethods;

  React.useEffect(() => {
    reset(defaultValues);
  }, [defaultValues]);

  const onSubmit = React.useCallback(
    (e: React.FormEvent) =>
      handleSubmit((data) => {
        updateQuery({
          ...query,
          ...mapFormDataToColumns(data),
        });

        setIsOpen(false);
      })(e),
    [handleSubmit],
  );

  const handleClose = () => {
    reset();
    setIsOpen(false);
  };

  return (
    <>
      <Button
        className={classNames(
          { 'bg-grey-100': isOpen },
          'text-base text-text-primary hover:bg-grey-100',
        )}
        ref={columnButtonRef}
        onClick={() => setIsOpen(true)}
        endIcon={<ExpandMoreIcon />}
        data-testid="columns-button"
      >
        {translate('COLUMNS')}
      </Button>
      <Popover
        open={isOpen}
        anchorEl={columnButtonRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handleClose}
        classes={{
          paper: 'h-full max-h-140',
        }}
      >
        <FormProvider {...formMethods}>
          <form onSubmit={onSubmit} className="flex h-full grow flex-col">
            <legend className="type-body-4 p-3">
              {translate('DISPLAY_COLUMNS')}
            </legend>
            <div className="w-72 grow overflow-y-scroll">
              <div className="grid grid-cols-1">
                {BaseColumnOptions.map((id) => (
                  <FormControlLabel
                    key={id}
                    control={
                      <ControlledSwitch
                        control={control}
                        name={id}
                        data-testid={`columns-switch-${id}`}
                      />
                    }
                    label={translate(ColumnsLabelMap[id])}
                    className="mb-3 px-3"
                  />
                ))}
                <ConstituentColumnSelectSubMenu
                  label={translate('SOCIAL_MEDIA')}
                  columns={SocialMediaColumns}
                />
                <ConstituentColumnSelectSubMenu
                  label={translate('DONATION_HISTORY')}
                  columns={ContributionHistoryColumns}
                />
                <ConstituentColumnSelectSubMenu
                  label={translate('CUSTOM_FIELDS')}
                  customFields={customFieldData}
                />
              </div>
            </div>
            <div className="flex flex-row-reverse border-t border-grey-300 px-2 py-3">
              <Button
                color="primary"
                type="submit"
                variant="contained"
                data-testid="columns-select-submit"
              >
                {translate('APPLY')}
              </Button>
              <Button onClick={handleClose} type="button" variant="text">
                {translate('CANCEL')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </Popover>
    </>
  );
}

export default ConstituentColumnSelect;
